import React, { FC, ReactNode, useEffect } from 'react';
import { FetchMoreOptions } from 'apollo-client';

import { LoaderTypes } from 'types/app';
import { GetFeedQuery, GetFeedQueryVariables, useGetFeedQuery } from 'api';
import { QueryManager } from 'components';
import { useRefetcher } from 'hooks';

export interface IFeedOptions {
  onLoadMore: () => void;
  exhausted: boolean;
  loading: boolean;
  count: number;
}

interface IProps {
  variables: GetFeedQueryVariables;
  children: (data: GetFeedQuery['getFeed'], options: IFeedOptions) => ReactNode;
  loader?: LoaderTypes;
}

const GetFeed: FC<IProps> = ({ children, loader, variables }) => {
  const { reset, refetchId } = useRefetcher();
  const queryResult = useGetFeedQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables,
  });

  const { data, error, loading, fetchMore, refetch } = queryResult;

  useEffect(() => {
    if (refetchId) {
      refetch();
      reset();
    }
  }, [refetchId]);

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    if (!options.fetchMoreResult) {
      return prev;
    }

    const results = [...prev.getFeed.results, ...options.fetchMoreResult.getFeed.results];

    return {
      ...prev,
      getFeed: { ...options.fetchMoreResult.getFeed, results },
    };
  };

  function handleLoadMore() {
    const { getFeed } = data;
    const { id_lt, offset } = getFeed;

    if (loading || error || (!id_lt && !offset)) {
      return Promise.resolve();
    }

    return fetchMore({
      updateQuery,
      variables: { id_lt, offset },
    });
  }

  return (
    <QueryManager loader={loader} {...queryResult}>
      {() => {
        if (!data?.getFeed) {
          return null;
        }

        const { getFeed } = data;
        const { id_lt, offset } = getFeed;

        return children(data.getFeed, {
          loading,
          exhausted: !id_lt && !offset,
          onLoadMore: handleLoadMore,
          count: data.getFeed.results?.length,
        });
      }}
    </QueryManager>
  );
};

export default GetFeed;
